import React, {FC, useEffect, useState} from "react";
import {Button, Spin, Switch, Tag} from "antd";
import {EnvironmentOutlined, RightOutlined} from "@ant-design/icons";
import {groupLessonsByDay, formatGroupHeader, formatCardDate, formatTimeRange} from "./assistantDateUtils";
import {useGetCompanyInfoQuery, useGetMyAssignmentsAssistantAppQuery} from "../../generated/graphql";
import {useLastUpdated} from "./LastUpdatedContext";
import styles from "./assistantApp.less";

const AssistantMyAssignments: FC = () => {
    const {setLastUpdated} = useLastUpdated();
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const featureAssistantApp = companyInfoData?.companyInfo?.subscription?.featureAssistantApp;
    const [includePast, setIncludePast] = useState(false);

    const {data: assignmentsData, loading, error, refetch} = useGetMyAssignmentsAssistantAppQuery({
        variables: {includePast},
        skip: featureAssistantApp === false,
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (assignmentsData && !loading) {
            setLastUpdated(new Date());
        }
    }, [assignmentsData, loading, setLastUpdated]);

    const assignments = assignmentsData?.getMyAssignmentsAssistantApp ?? [];

    if (featureAssistantApp === false) {
        return (
            <div className={styles.assistantFeatureDisabled}>
                <Tag color="volcano">{"Feature nicht freigeschalten"}</Tag>
            </div>
        );
    }

    const dayGroups = groupLessonsByDay(assignments);

    return (
        <div>
            <div className={styles.assistantSectionTitle}>{"Meine Dienste"}</div>

            <div className={styles.assistantToggleRow}>
                <Switch checked={includePast} onChange={setIncludePast} />
                <span>{"Vergangene anzeigen"}</span>
            </div>

            {error && !loading ? (
                <div className={styles.assistantEmpty}>
                    {"Fehler beim Laden. "}
                    <Button type="link" onClick={() => refetch()}>{"Erneut versuchen"}</Button>
                </div>
            ) : loading && assignments.length === 0 ? (
                <div style={{textAlign: "center", padding: 48}}>
                    <Spin />
                </div>
            ) : assignments.length === 0 ? (
                <div className={styles.assistantEmpty}>{"Keine Dienste gefunden."}</div>
            ) : (
                dayGroups.map((group) => (
                    <div key={group.date.toISOString()} className={styles.assistantDateGroup}>
                        <div className={styles.assistantDateGroupHeader}>{formatGroupHeader(group.date)}</div>
                        {group.lessons.map((item) => {
                            const lesson = item.courseLesson;
                            const courseLabel = [lesson.course?.courseType?.name, lesson.course?.prefixedCourseNumber].filter(Boolean).join(" · ");
                            const locationParts = [lesson.location?.name, lesson.location?.city].filter(Boolean);
                            const locationLabel = locationParts.join(", ");
                            const venueLabel = lesson.venue?.name || "";
                            const timeRange = formatTimeRange(lesson.startDateTime, lesson.endDateTime);

                            return (
                                <div key={lesson.id} className={styles.assistantLgCard}>
                                    {/* Header: Nur Kurslabel, kein Button */}
                                    <div className={styles.assistantLgCardHeader}>
                                        <span className={styles.assistantLgCardCourseLabel}>{courseLabel || "Kurseinheit"}</span>
                                    </div>

                                    {/* Grosses Datum */}
                                    <div className={styles.assistantLgCardBigDate}>{formatCardDate(new Date(lesson.startDateTime))}</div>

                                    {/* Uhrzeit orange */}
                                    <div className={styles.assistantLgCardTime}>
                                        <span className={styles.assistantLgCardTimeDot}>{"●"}</span> {timeRange}
                                        {" Uhr"}
                                    </div>

                                    {/* Location + Personen-Sektion */}
                                    <div className={styles.assistantLgCardLocationSection}>
                                        {locationLabel || venueLabel ? (
                                            <div className={styles.assistantLgCardLocationRow}>
                                                <EnvironmentOutlined className={styles.assistantLgCardLocationPin} />
                                                <span className={styles.assistantLgCardLocationText}>{[locationLabel, venueLabel].filter(Boolean).join(" · ")}</span>
                                                <RightOutlined className={styles.assistantLgCardLocationArrow} />
                                            </div>
                                        ) : null}

                                        {/* KL/AS Tags */}
                                        <div className={styles.assistantLgCardPersonTags}>
                                            {lesson.instructors.map((instructor) => {
                                                const name = [instructor.firstname, instructor.lastname].filter(Boolean).join(" ");
                                                return (
                                                    <span key={instructor.id} className={`${styles.assistantLgCardPersonTag} ${styles.assistantLgCardPersonTagKl}`}>
                                                        <span className={`${styles.assistantLgCardPersonBadge} ${styles.assistantLgCardPersonBadgeKl}`}>{"KL"}</span>
                                                        {name}
                                                    </span>
                                                );
                                            })}
                                            {item.assignedAssistants && item.assignedAssistants.length > 0
                                                ? item.assignedAssistants.map((a, idx) => {
                                                      const name = [a.firstname, a.lastname].filter(Boolean).join(" ");
                                                      return (
                                                          <span key={`as-${idx}`} className={`${styles.assistantLgCardPersonTag} ${styles.assistantLgCardPersonTagAs}`}>
                                                              <span className={`${styles.assistantLgCardPersonBadge} ${styles.assistantLgCardPersonBadgeAs}`}>{"AS"}</span>
                                                              {name}
                                                          </span>
                                                      );
                                                  })
                                                : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))
            )}
        </div>
    );
};

export default AssistantMyAssignments;
